import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  KNOWLEDGE_GRAPH_NODE_CAP,
  SUBJECT_VOCABULARY_BY_VERTICAL,
  statusTone,
  type KnowledgeGraph,
  type KnowledgeGraphNode,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { loadErrorLine } from '../../api/errors';
import { useKnowledgeGraph, useKnowledgePanel } from '../../api/hooks';
import {
  KNOWLEDGE_KIND_CHIPS,
  KNOWLEDGE_PANEL_CLOSE,
  WHOLE_GRAPH_CHOOSE_ACCOUNT,
  WHOLE_GRAPH_DRAW_ANYWAY,
  WHOLE_GRAPH_EMPTY,
  WHOLE_GRAPH_NONE_SHOWN,
  WHOLE_GRAPH_NOT_TRACKED,
  WHOLE_GRAPH_FILTER_LABEL,
  WHOLE_GRAPH_TITLE,
  WHOLE_GRAPH_TOO_SLOW,
  subjectStatusLabel,
  wholeGraphLargeWarning,
  wholeGraphLayingOut,
  wholeGraphShowing,
  wholeGraphTooLarge,
  wholeGraphTotals,
} from '../../lib/copy';
import { orgHasProtectionAccounts } from '../../lib/vertical';
import {
  KIND_CHIP,
  LARGE_GRAPH,
  NOT_TRACKED,
  boundsOf,
  LABEL_MARK_LIMIT,
  markAt,
  placeLabels,
  type LabelCandidate,
  filterGraph,
  fitView,
  layoutWholeGraph,
  zoomAt,
  type IndexLink,
  type View,
  type WholeLayout,
} from '../../lib/whole-graph';
import type { WorkerReply } from '../../lib/whole-graph.worker';
import { GraphMark } from '../../lib/graph-marks';
import { grownScale, usePinch, wheelFactor } from '../../lib/graph-zoom';
import { truncateLabel } from '../../lib/network-graph';
import { AccountChoice } from '../accounts/account-choice';
import { Modal } from '../ui/modal';
import { KnowledgePanelView } from './knowledge-panel';
import { Loading } from '../ui/loading';
import { EmptyLine } from '../ui/empty-line';
import './whole-graph.css';

/**
 * **The whole graph** — every tie the organization holds, in one picture,
 * opened from the knowledge base's footer so an owner can see how much is on
 * record and how it clusters.
 *
 * Deliberately minimal: dots and lines, painted on a canvas. A name prints under its dot only where
 * it fits clear of every other name and dot, and a dense picture is dots only
 * until a zoom spreads it (`placeLabels`). Every dot says its name on hover; a
 * click opens the ordinary knowledge panel beside the picture, read only. In a
 * security organization the picture is one protection account at a time,
 * because accounts are walls.
 */
export function WholeGraphView({
  initialAccountId,
  onClose,
}: {
  initialAccountId: string | undefined;
  onClose: () => void;
}) {
  const org = useOrg();
  const security = orgHasProtectionAccounts(org);
  const [accountId, setAccountId] = useState(initialAccountId ?? '');
  const ready = !security || accountId !== '';
  const account = security && accountId !== '' ? accountId : undefined;
  const graph = useKnowledgeGraph(ready, account);
  const [selected, setSelected] = useState<string | null>(null);
  const panel = useKnowledgePanel(selected, account);
  /* The filter chips that are off. Empty is everybody, which is the default. */
  const [off, setOff] = useState<ReadonlySet<string>>(() => new Set());
  const shown = useMemo(
    () => (graph.data ? filterGraph(graph.data, off) : undefined),
    [graph.data, off],
  );
  const toggle = (chip: string) =>
    setOff((current) => {
      const next = new Set(current);
      if (next.has(chip)) next.delete(chip);
      else next.add(chip);
      return next;
    });

  return (
    <Modal label={WHOLE_GRAPH_TITLE} full onClose={onClose}>
      <div className="whole-graph">
        <div className="whole-graph-head">
          <div className="modal-head">{WHOLE_GRAPH_TITLE}</div>
          {security ? (
            <AccountChoice
              value={accountId}
              onChange={(id) => {
                setAccountId(id);
                setSelected(null);
              }}
            />
          ) : null}
          {graph.data?.totals ? (
            <div className="whole-graph-totals">
              {wholeGraphTotals(graph.data.totals)}
              {shown && off.size > 0 && graph.data.drawn
                ? ` · ${wholeGraphShowing(shown.nodes.length, graph.data.nodes.length)}`
                : ''}
            </div>
          ) : null}
          <button type="button" className="linkish flush whole-graph-close" onClick={onClose}>
            {KNOWLEDGE_PANEL_CLOSE}
          </button>
        </div>

        {graph.data?.drawn ? (
          <FilterChips vertical={org.vertical} off={off} onToggle={toggle} />
        ) : null}

        <div className="whole-graph-body">
          <div className="whole-graph-stage">
            {!ready ? (
              <EmptyLine>{WHOLE_GRAPH_CHOOSE_ACCOUNT}</EmptyLine>
            ) : graph.isPending ? (
              <EmptyLine>
                <Loading />
              </EmptyLine>
            ) : graph.isError ? (
              <EmptyLine>{loadErrorLine(graph.error)}</EmptyLine>
            ) : (
              <Stage
                /* A fresh stage per account: agreeing to draw one large graph
                   is not agreeing to draw the next. */
                key={accountId}
                full={graph.data}
                shown={shown ?? graph.data}
                vertical={org.vertical}
                selected={selected}
                onSelect={setSelected}
              />
            )}
          </div>

          {selected !== null ? (
            <aside className="whole-graph-side">
              {panel.isPending ? (
                <EmptyLine>
                  <Loading />
                </EmptyLine>
              ) : panel.isError ? (
                <EmptyLine>{loadErrorLine(panel.error)}</EmptyLine>
              ) : (
                <KnowledgePanelView
                  panel={panel.data}
                  vertical={org.vertical}
                  onClose={() => setSelected(null)}
                  onSelect={setSelected}
                  track={null}
                  trackOrganisation={null}
                  onLink={null}
                  onRename={null}
                  onJoin={null}
                />
              )}
            </aside>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/**
 * **What is on the picture**, as a legend that is also the filter: one entry
 * per status in the org's own vocabulary (in its tone), one for the people
 * nobody holds a record for, and one each for organizations and events, each
 * beside the mark it is drawn with. All on to begin with; turning one off hides
 * those records where they stand (with anything left tied to nothing on
 * screen), so the reader's zoom and the shape they were looking at both survive
 * the press.
 */
function FilterChips({
  vertical,
  off,
  onToggle,
}: {
  vertical: string;
  off: ReadonlySet<string>;
  onToggle: (chip: string) => void;
}) {
  const statuses = SUBJECT_VOCABULARY_BY_VERTICAL[vertical]?.statuses ?? [];
  const chips: {
    key: string;
    label: string;
    kind: KnowledgeGraphNode['kind'];
    tone?: string;
  }[] = [
    ...statuses.map((status) => ({
      key: status as string,
      label: subjectStatusLabel(vertical, status) ?? status,
      kind: 'person' as const,
      tone: statusTone(vertical, status),
    })),
    { key: NOT_TRACKED, label: WHOLE_GRAPH_NOT_TRACKED, kind: 'person' },
    { key: KIND_CHIP.org, label: KNOWLEDGE_KIND_CHIPS.org, kind: 'org' },
    { key: KIND_CHIP.event, label: KNOWLEDGE_KIND_CHIPS.event, kind: 'event' },
  ];
  return (
    <div className="whole-graph-legend" role="group" aria-label={WHOLE_GRAPH_FILTER_LABEL}>
      {chips.map((chip) => {
        const on = !off.has(chip.key);
        return (
          <button
            key={chip.key}
            type="button"
            className={on ? 'on' : undefined}
            aria-pressed={on}
            onClick={() => onToggle(chip.key)}
          >
            <svg
              className={`whole-graph-swatch ${chip.kind}`}
              viewBox="-6 -6 12 12"
              aria-hidden="true"
              data-tone={chip.tone}
            >
              <GraphMark kind={chip.kind} r={chip.kind === 'person' ? 4 : 3.8} />
            </svg>
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * What the stage shows in place of a picture, or the picture. The layout is of
 * the `full` graph and never redone for a chip: `shown` is drawn at the
 * positions the full graph gave it. A large graph is laid out only once the
 * reader says to, since the layout and the first draw can hold the tab for a
 * while.
 */
function Stage({
  full,
  shown,
  vertical,
  selected,
  onSelect,
}: {
  full: KnowledgeGraph;
  shown: KnowledgeGraph;
  vertical: string;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const large = full.nodes.length > LARGE_GRAPH;
  const [agreed, setAgreed] = useState(false);
  const { layout, progress } = useWholeLayout(full, !large || agreed);

  if (full.totals === null) return <EmptyLine>{WHOLE_GRAPH_TOO_SLOW}</EmptyLine>;
  if (!full.drawn) return <EmptyLine>{wholeGraphTooLarge(KNOWLEDGE_GRAPH_NODE_CAP)}</EmptyLine>;
  if (full.nodes.length === 0) return <EmptyLine>{WHOLE_GRAPH_EMPTY}</EmptyLine>;
  if (large && !agreed) {
    return (
      <div className="whole-graph-ask">
        <p>{wholeGraphLargeWarning(full.nodes.length)}</p>
        <button type="button" className="btn act" onClick={() => setAgreed(true)}>
          {WHOLE_GRAPH_DRAW_ANYWAY}
        </button>
      </div>
    );
  }
  if (layout === null) {
    return <EmptyLine>{wholeGraphLayingOut(full.nodes.length, progress)}</EmptyLine>;
  }

  return (
    <>
      <Canvas
        full={full}
        shown={shown}
        layout={layout}
        vertical={vertical}
        selected={selected}
        onSelect={onSelect}
      />
      {shown.nodes.length === 0 ? (
        <div className="whole-graph-none">{WHOLE_GRAPH_NONE_SHOWN}</div>
      ) : null}
    </>
  );
}

/**
 * The layout, placed in a Web Worker so the page stays responsive while
 * thousands of dots settle. Null until it lands, with how far along it is
 * meanwhile. Nothing starts until `start`. A reply for a graph that has since
 * been replaced is dropped with its worker.
 */
function useWholeLayout(
  graph: KnowledgeGraph,
  start: boolean,
): { layout: WholeLayout | null; progress: number } {
  const [placed, setPlaced] = useState<{ graph: KnowledgeGraph; layout: WholeLayout } | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!start || !graph.drawn || graph.nodes.length === 0) return;
    setProgress(0);
    const index = new Map(graph.nodes.map((node, i) => [node.id, i]));
    const links: IndexLink[] = [];
    for (const edge of graph.edges) {
      const a = index.get(edge.a);
      const b = index.get(edge.b);
      if (a !== undefined && b !== undefined) links.push([a, b]);
    }

    if (typeof Worker === 'undefined') {
      setPlaced({ graph, layout: layoutWholeGraph(graph.nodes.length, links) });
      return;
    }
    const worker = new Worker(new URL('../../lib/whole-graph.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (event: MessageEvent<WorkerReply>) => {
      if ('progress' in event.data) {
        setProgress(event.data.progress);
        return;
      }
      setPlaced({ graph, layout: event.data.layout });
      worker.terminate();
    };
    worker.postMessage({ count: graph.nodes.length, links });
    return () => worker.terminate();
  }, [graph, start]);

  return { layout: placed?.graph === graph ? placed.layout : null, progress };
}

/** Pointer travel under which a press is a click rather than a drag. */
const CLICK_SLOP = 4;
const TIP_OFFSET = 12;
/** Screen pixels around a mark that still count as pointing at it. */
const HIT_SLOP = 2;

/**
 * The picture: every line and every mark painted on one canvas, with the
 * selection, the hovered mark and the names drawn over it in SVG. A canvas
 * paints tens of thousands of marks in a few milliseconds where as many SVG
 * elements make every pan and zoom stutter, so a mark has no element of its
 * own: the pointer is matched to the nearest mark under it (`markAt`).
 */
function Canvas({
  full,
  shown,
  layout,
  vertical,
  selected,
  onSelect,
}: {
  full: KnowledgeGraph;
  shown: KnowledgeGraph;
  layout: WholeLayout;
  vertical: string;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const paper = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [view, setView] = useState<View | null>(null);
  /* The scale the layout was fitted at: no dot is drawn smaller than it was there. */
  const [fitK, setFitK] = useState(1);
  const [tip, setTip] = useState<{ index: number; x: number; y: number } | null>(null);
  const press = useRef<{ x: number; y: number; view: View; moved: boolean } | null>(null);

  useLayoutEffect(() => {
    const node = box.current;
    if (!node) return;
    const measure = () => setSize({ w: node.clientWidth, h: node.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /* Fit once per layout; after that the view is the reader's, and a resize —
     the details pane opening beside it — keeps the same middle in the middle
     rather than refitting over the reader's zoom. */
  const fitted = useRef<{ layout: WholeLayout; w: number; h: number } | null>(null);
  useEffect(() => {
    if (!size) return;
    const last = fitted.current;
    fitted.current = { layout, w: size.w, h: size.h };
    if (!last || last.layout !== layout) {
      const fit = fitView(boundsOf(layout), size.w, size.h);
      setView(fit);
      setFitK(fit.k);
      return;
    }
    const dx = (size.w - last.w) / 2;
    const dy = (size.h - last.h) / 2;
    if (dx !== 0 || dy !== 0)
      setView((current) =>
        current ? { ...current, x: current.x + dx, y: current.y + dy } : current,
      );
  }, [layout, size]);

  /* React registers wheel listeners as passive, and a passive listener cannot
     stop the page behind the modal from scrolling, so this one is bound by hand. */
  useEffect(() => {
    const node = box.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const factor = wheelFactor(event);
      setView((current) =>
        current
          ? zoomAt(current, factor, event.clientX - rect.left, event.clientY - rect.top)
          : current,
      );
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, []);

  usePinch(
    box,
    (factor, clientX, clientY) => {
      const rect = box.current?.getBoundingClientRect();
      if (!rect) return;
      setView((current) =>
        current ? zoomAt(current, factor, clientX - rect.left, clientY - rect.top) : current,
      );
    },
    () => {
      press.current = null;
      setTip(null);
    },
  );

  /* Positions are the full graph's, so every index here is into `full.nodes`. */
  const index = useMemo(() => new Map(full.nodes.map((node, i) => [node.id, i])), [full]);
  const neighbours = useMemo(() => {
    const out = new Map<number, number[]>();
    const add = (from: number, to: number) => {
      const held = out.get(from);
      if (held) held.push(to);
      else out.set(from, [to]);
    };
    for (const edge of shown.edges) {
      const a = index.get(edge.a);
      const b = index.get(edge.b);
      if (a === undefined || b === undefined) continue;
      add(a, b);
      add(b, a);
    }
    return out;
  }, [shown, index]);
  const visible = useMemo(() => new Set(shown.nodes.map((node) => node.id)), [shown]);
  /* The marks on the picture, as indices into the layout, in drawing order. */
  const marks = useMemo(() => {
    const out: number[] = [];
    for (const node of shown.nodes) {
      const at = index.get(node.id);
      if (at !== undefined) out.push(at);
    }
    return out;
  }, [shown, index]);
  const edgeIndices = useMemo(() => {
    const out: IndexLink[] = [];
    for (const edge of shown.edges) {
      const a = index.get(edge.a);
      const b = index.get(edge.b);
      if (a !== undefined && b !== undefined) out.push([a, b]);
    }
    return out;
  }, [shown, index]);

  /* The fit fills the panel, so on a small graph it can be well above the
     marks' cap: the marks hold at the cap and the positions spread. */
  const markScale = view ? grownScale(view.k, Math.min(fitK, DOT_CAP_SCALE), DOT_CAP_SCALE) : 1;
  const radius = (at: number) => radiusOf(full.nodes[at]!);
  const [color] = useState(colorReader);

  useLayoutEffect(() => {
    const canvas = paper.current;
    if (!canvas || !size || !view) return;
    paint(canvas, {
      size,
      view,
      markScale,
      layout,
      marks,
      edges: edgeIndices,
      nodes: full.nodes,
      vertical,
      color,
    });
  }, [size, view, markScale, layout, marks, edgeIndices, full, vertical, color]);

  const labels = useMemo(() => {
    if (!size || !view) return [];
    /* Only marks on screen are candidates, and past the limit the picture is
       dots only: a wheel tick over a large graph builds nothing. */
    const candidates: LabelCandidate[] = [];
    for (const at of marks) {
      const x = layout.x[at]! * view.k + view.x;
      const y = layout.y[at]! * view.k + view.y;
      if (x < 0 || x > size.w || y < 0 || y > size.h) continue;
      if (candidates.length === LABEL_MARK_LIMIT) return [];
      const node = full.nodes[at]!;
      candidates.push({
        index: at,
        x,
        y,
        r: radiusOf(node) * view.k * markScale,
        text: truncateLabel(node.name, LABEL_MAX_CHARS),
        rank: (node.tracked ? 0 : LABEL_UNTRACKED_RANK) - (neighbours.get(at)?.length ?? 0),
      });
    }
    return placeLabels(candidates, measureName, size.w, size.h, LABEL_LINE);
  }, [marks, full, layout, view, size, markScale, neighbours]);

  const selectedIndex =
    selected === null || !visible.has(selected) ? undefined : index.get(selected);
  const hitAt = (element: HTMLElement, clientX: number, clientY: number): number | null => {
    if (!view) return null;
    const rect = element.getBoundingClientRect();
    return markAt(
      layout,
      marks,
      view,
      clientX - rect.left,
      clientY - rect.top,
      (at) => radius(at) * view.k * markScale + HIT_SLOP,
    );
  };

  return (
    <div
      ref={box}
      className={tip ? 'whole-graph-canvas over' : 'whole-graph-canvas'}
      onPointerDown={(event) => {
        if (!view) return;
        press.current = { x: event.clientX, y: event.clientY, view, moved: false };
        setTip(null);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const held = press.current;
        if (held) {
          const dx = event.clientX - held.x;
          const dy = event.clientY - held.y;
          if (!held.moved && Math.hypot(dx, dy) > CLICK_SLOP) held.moved = true;
          if (held.moved) {
            setTip(null);
            setView({ ...held.view, x: held.view.x + dx, y: held.view.y + dy });
          }
          return;
        }
        const hit = hitAt(event.currentTarget, event.clientX, event.clientY);
        if (hit === null) {
          setTip(null);
          return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        setTip({ index: hit, x: event.clientX - rect.left, y: event.clientY - rect.top });
      }}
      onPointerUp={(event) => {
        const held = press.current;
        press.current = null;
        if (held && !held.moved) {
          const hit = hitAt(event.currentTarget, event.clientX, event.clientY);
          if (hit !== null) onSelect(full.nodes[hit]!.id);
        }
      }}
      onPointerCancel={() => {
        press.current = null;
      }}
      onPointerLeave={() => setTip(null)}
    >
      {size ? (
        <canvas
          ref={paper}
          className="whole-graph-paper"
          width={Math.round(size.w * pixelRatio())}
          height={Math.round(size.h * pixelRatio())}
          style={{ width: size.w, height: size.h }}
        />
      ) : null}
      {size && view ? (
        <svg
          width={size.w}
          height={size.h}
          className="whole-graph-svg"
          pointerEvents="none"
          /* Marks grow with the zoom up to a readable size and then hold, so a
             dense cluster opens up rather than filling with bigger dots. */
          style={{ '--mark-scale': markScale } as CSSProperties}
        >
          <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
            {selectedIndex !== undefined ? (
              <Selection
                at={selectedIndex}
                neighbours={neighbours.get(selectedIndex) ?? []}
                layout={layout}
              />
            ) : null}
            {tip ? (
              <GraphMark
                kind={full.nodes[tip.index]!.kind}
                x={layout.x[tip.index]!}
                y={layout.y[tip.index]!}
                r={radius(tip.index)}
                className="whole-graph-hover"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
          </g>
          {labels.length > 0 ? (
            <g className="whole-graph-labels" aria-hidden="true">
              {labels.map((label) => (
                <text key={label.index} x={label.x} y={label.top + LABEL_BASELINE}>
                  {label.text}
                </text>
              ))}
            </g>
          ) : null}
        </svg>
      ) : null}
      {tip ? (
        <div className="network-tip" style={{ left: tip.x + TIP_OFFSET, top: tip.y + TIP_OFFSET }}>
          {tipText(full.nodes[tip.index]!, vertical)}
        </div>
      ) : null}
    </div>
  );
}

const KIND_WORD: Record<KnowledgeGraphNode['kind'], string> = {
  person: 'Person',
  org: 'Organization',
  event: 'Event',
};

function tipText(node: KnowledgeGraphNode, vertical: string): string {
  const status = node.status === null ? null : subjectStatusLabel(vertical, node.status);
  return `${node.name} · ${status ?? KIND_WORD[node.kind]}`;
}

/* A name under its dot: Newsreader at the Network tab's name size. */
const LABEL_FONT = '450 13.5px Newsreader';
const LABEL_LINE = 16;
const LABEL_BASELINE = 12;
const LABEL_MAX_CHARS = 28;
/** Tracked people place their names first, then the most connected. */
const LABEL_UNTRACKED_RANK = 10_000;

let measuring: CanvasRenderingContext2D | null | undefined;

/** A name's width from a canvas; an estimate where there is no 2D context. */
function measureName(text: string): number {
  if (measuring === undefined) {
    try {
      measuring = document.createElement('canvas').getContext('2d');
    } catch {
      measuring = null;
    }
  }
  if (!measuring) return text.length * 6.6;
  measuring.font = LABEL_FONT;
  return measuring.measureText(text).width;
}

const DOT = 4;
const TRACKED_DOT = 6;
const radiusOf = (node: KnowledgeGraphNode) => (node.tracked ? TRACKED_DOT : DOT);
/** The most a dot is magnified on screen: a tracked dot then reads 15px across. */
const DOT_CAP_SCALE = 1.25;

/* The Network tab's grammar at the whole graph's scale (blend spec, Network
   graph): hairline ties, a tracked person filled in their status tone, and
   everyone else a pale tint of their kind. The colors are the tokens', read
   off the page so the canvas and the legend cannot disagree. */
const EDGE_WIDTH = 0.6;
const EDGE_OPACITY = 0.35;
const UNTRACKED: Record<KnowledgeGraphNode['kind'], { fill: string; stroke: string }> = {
  person: { fill: '--graph-untracked-person', stroke: '--graph-untracked-person-stroke' },
  org: { fill: '--graph-untracked-org', stroke: '--graph-untracked-org-stroke' },
  event: { fill: '--graph-untracked-event', stroke: '--graph-untracked-event-stroke' },
};

const pixelRatio = () => (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1);

type ColorOf = (token: string, tone?: string) => string;

/**
 * A CSS color the canvas can use: a token, or a status tone through the same
 * `[data-tone]` table the legend and the People table read. Each is read once
 * from a probe element and kept.
 */
function colorReader(): ColorOf {
  const seen = new Map<string, string>();
  return (token, tone) => {
    const key = `${tone ?? ''}|${token}`;
    const held = seen.get(key);
    if (held !== undefined) return held;
    const probe = document.createElement('span');
    probe.style.display = 'none';
    probe.style.color = `var(${token})`;
    if (tone !== undefined) probe.setAttribute('data-tone', tone);
    document.body.appendChild(probe);
    const color = getComputedStyle(probe).color;
    probe.remove();
    seen.set(key, color);
    return color;
  };
}

/** Every line, then every mark, in screen pixels so strokes stay hairlines at any zoom. */
function paint(
  canvas: HTMLCanvasElement,
  {
    size,
    view,
    markScale,
    layout,
    marks,
    edges,
    nodes,
    vertical,
    color,
  }: {
    size: { w: number; h: number };
    view: View;
    markScale: number;
    layout: WholeLayout;
    marks: readonly number[];
    edges: readonly IndexLink[];
    nodes: readonly KnowledgeGraphNode[];
    vertical: string;
    color: ColorOf;
  },
) {
  const context = canvas.getContext('2d');
  if (!context) return;
  const ratio = canvas.width / Math.max(size.w, 1);
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, size.w, size.h);
  const sx = (at: number) => layout.x[at]! * view.k + view.x;
  const sy = (at: number) => layout.y[at]! * view.k + view.y;

  context.beginPath();
  for (const [a, b] of edges) {
    context.moveTo(sx(a), sy(a));
    context.lineTo(sx(b), sy(b));
  }
  context.globalAlpha = EDGE_OPACITY;
  context.lineWidth = EDGE_WIDTH;
  context.strokeStyle = color('--faint');
  context.stroke();
  context.globalAlpha = 1;

  /* One path per look, untracked first, so the toned marks sit on top. */
  const groups = new Map<string, { fill: string; stroke: string; marks: number[] }>();
  for (const at of marks) {
    const node = nodes[at]!;
    const tone = node.status === null ? undefined : statusTone(vertical, node.status);
    const key = tone === undefined ? `kind:${node.kind}` : `tone:${tone}`;
    let group = groups.get(key);
    if (!group) {
      group =
        tone === undefined
          ? {
              fill: color(UNTRACKED[node.kind].fill),
              stroke: color(UNTRACKED[node.kind].stroke),
              marks: [],
            }
          : { fill: color('--tone', tone), stroke: color('--sheet'), marks: [] };
      groups.set(key, group);
    }
    group.marks.push(at);
  }
  const unit = view.k * markScale;
  const ordered = [...groups.entries()].sort(([a], [b]) =>
    a.startsWith('kind:') === b.startsWith('kind:') ? 0 : a.startsWith('kind:') ? -1 : 1,
  );
  context.lineWidth = 1;
  for (const [, group] of ordered) {
    context.beginPath();
    for (const at of group.marks) {
      const x = sx(at);
      const y = sy(at);
      const node = nodes[at]!;
      const r = radiusOf(node) * unit;
      if (x < -r - 2 || y < -r - 2 || x > size.w + r + 2 || y > size.h + r + 2) continue;
      traceMark(context, node.kind, x, y, r, unit);
    }
    context.fillStyle = group.fill;
    context.fill();
    context.strokeStyle = group.stroke;
    context.stroke();
  }
}

/** The graph registry's shapes (`graph-marks.tsx`), traced onto a canvas path. */
function traceMark(
  context: CanvasRenderingContext2D,
  kind: KnowledgeGraphNode['kind'],
  x: number,
  y: number,
  r: number,
  unit: number,
) {
  if (kind === 'org') {
    context.moveTo(x + r, y);
    context.rect(x - r, y - r, r * 2, r * 2);
    return;
  }
  if (kind === 'event') {
    const d = r + unit;
    context.moveTo(x, y - d);
    context.lineTo(x + d, y);
    context.lineTo(x, y + d);
    context.lineTo(x - d, y);
    context.closePath();
    return;
  }
  context.moveTo(x + r, y);
  context.arc(x, y, r, 0, Math.PI * 2);
}

/** The chosen dot and its ties, drawn over the picture. */
function Selection({
  at,
  neighbours,
  layout,
}: {
  at: number;
  neighbours: readonly number[];
  layout: WholeLayout;
}) {
  const x = layout.x[at]!;
  const y = layout.y[at]!;
  const d = neighbours.map((n) => `M${x} ${y}L${layout.x[n]} ${layout.y[n]}`).join('');
  return (
    <g>
      <path className="whole-graph-edges hot" d={d} vectorEffect="non-scaling-stroke" />
      <circle
        className="whole-graph-ring"
        cx={x}
        cy={y}
        r={TRACKED_DOT + 3}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}
